import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { z } from "zod";

import {
  resetPasswordRequestSchema,
  type ResetPasswordRequest,
} from "@sendsms/shared";
import { api } from "@/lib/api";
import {
  applyFieldErrors,
  translateApiError,
  translateFieldError,
} from "@/lib/form-errors";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

// Extend the server-side schema with client-side confirm_password.
// The server only takes (token, new_password); we enforce the match client-side.
const formSchema = resetPasswordRequestSchema
  .extend({ confirm_password: z.string() })
  .refine((v) => v.new_password === v.confirm_password, {
    message: "passwords_dont_match",
    path: ["confirm_password"],
  });

type FormValues = z.infer<typeof formSchema>;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [showPassword, setShowPassword] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      token,
      new_password: "",
      confirm_password: "",
    },
    mode: "onSubmit",
  });

  const mutation = useMutation({
    mutationFn: (body: ResetPasswordRequest) => api.resetPassword(body),
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (err: unknown) => {
      const handled = applyFieldErrors(t, err, form.setError);
      if (!handled) setTopError(translateApiError(t, err));
    },
  });

  if (!token) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.reset_password.invalid_token_title")}</CardTitle>
          <CardSubtitle>
            {t("auth.reset_password.invalid_token_message")}
          </CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link
            to="/forgot-password"
            className="text-caption font-medium text-brand-500 hover:underline"
          >
            {t("auth.forgot_password.title")}
          </Link>
        </CardBody>
      </Card>
    );
  }

  if (success) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
            <CheckCircle2 className="h-6 w-6 text-success-500" />
          </div>
          <CardTitle>{t("auth.reset_password.success_title")}</CardTitle>
          <CardSubtitle>
            {t("auth.reset_password.success_message")}
          </CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link
            to="/login"
            className="inline-flex w-full items-center justify-center gap-2 rounded-pill bg-brand-500 px-6 py-3 text-body font-medium text-white transition-colors hover:bg-brand-600 focus:outline-none focus:shadow-focus"
          >
            {t("auth.reset_password.sign_in_now")}
          </Link>
        </CardBody>
      </Card>
    );
  }

  const passwordError = form.formState.errors.new_password?.message
    ? translateFieldError(t, form.formState.errors.new_password.message)
    : undefined;
  const confirmError = form.formState.errors.confirm_password?.message
    ? translateFieldError(t, form.formState.errors.confirm_password.message)
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.reset_password.title")}</CardTitle>
        <CardSubtitle>{t("auth.reset_password.subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        {topError ? (
          <Alert tone="danger" description={topError} className="mb-5" />
        ) : null}

        <form
          onSubmit={form.handleSubmit((data) => {
            setTopError(null);
            mutation.mutate({
              token: data.token,
              new_password: data.new_password,
            });
          })}
          className="space-y-4"
          noValidate
        >
          <Input
            label={t("auth.reset_password.new_password_label")}
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            {...form.register("new_password")}
            error={passwordError}
            trailingElement={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? t("common.hide") : t("common.show")}
                className="flex h-8 w-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-surface-muted focus:outline-none focus-visible:shadow-focus"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />

          <Input
            label={t("auth.reset_password.confirm_password_label")}
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            {...form.register("confirm_password")}
            error={confirmError}
          />

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={mutation.isPending}
          >
            {t("auth.reset_password.submit")}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
